import {
  Component,
  OnDestroy,
  ElementRef,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from "@angular/core";
import { BfxBlotterOrdersActionService } from "../bfx-blotter-orders/bfx-blotter-orders-action.service";
import { BfxBlotterOrdersService } from "../bfx-blotter-orders/bfx-blotter-orders.service";
import { BfxDialogData } from "../bfx-dialogs/models/bfx-dialog-data.model";
import { BfxDialogCommand } from "../bfx-dialogs/models/bfx-dialog-command.enum";
import { GoldenLayoutComponent } from "../golden-layout/golden-layout-component";
import { TranslateService } from "@ngx-translate/core";
import { GoldenLayoutPopout } from "../golden-layout/golden-layout-popout";
import { Subscription } from "rxjs";
import { LoggerService } from "@bfx/logger";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

import {
  ErrorsImpl,
  AcceptanceAction,
  AcceptanceType,
  TradingGroupImpl,
  OrderPauseResumeRequestImpl,
  OrderPauseResumeResponseImpl,
  PauseResumeAction,
  StandardReportImpl,
  PauseState,
  ModelUpdate,
  OrderDataImpl,
  OrderType,
  OrderState,
} from "shared/cubiq-model-multi-asset";

import {
  Roles,
  ActiveUser,
  OrderDataActionPromiseService,
  OrdersTableDefinitionFactory,
  OrderDataPromiseLookupService,
  OrderAcceptancePromiseService,
  SystemConfigPromiseService,
  TableDefinition,
  ActiveUserProfilePromiseService,
  TraderGroupPromiseLookupService,
  COLUMN_DESK_ID,
  COLUMN_ORDER_STATE,
  BfxBlotterAction,
  HedgeOrderDataPrimaryLookupService,
  NotificationService,
  AlgoStrategyLookupService,
  OrderDataModelUpdateSource,
} from "shared/cubiq-services";
import { ColDef, GridOptions } from "ag-grid-community";

export const STATUS_FAILED: string = "Failed";
export const MIN_VISIBLE_ROWS: number = 7;

export enum OrderPauseResumeRecordState {
  IN_PROGRESS,
  ACCEPTANCE,
  FAILED,
  ABORT_REJECT,
  SUCCESS,
}

export class OrderPauseResumeRecord {
  constructor(
    public orderId: string = "",
    public orderRef: string = "",
    public strategyId: string = "",
    public orderDescription: string = "",
    public tgId: string = "",
    public traderGroupName: string = "",
    public orderStatus: string = "",
    public reason: string = "",
    public external: boolean = false,
    public state: OrderPauseResumeRecordState = OrderPauseResumeRecordState.IN_PROGRESS,
    public eligibleForPause: boolean = false,
    public orderState: OrderState = null,
  ) {}
}

import { ProgressStatus } from "../bfx-element-library/progress-bar-value.constants";
import {
  OrderPauseResumeLaunchData,
  OrderPauseResumeInstruction,
} from "./bfx-order-pauseresume.launcher";
import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
import { BfxColDef } from "shared/cubiq-services/model/TableDefinition";
import { BfxBaseBlotterColumnSelectorService } from "../bfx-base-blotter/bfx-base-blotter-columnselector.service";
import { FieldStateValue } from "shared/base-components/mixins/field-state";
import { NativeDesktopWindow } from "shared/native-desktop/NativeDesktopWindow";
import { OrderDescriptionHelper } from "shared/cubiq-services/model/orders/OrderDescriptionHelper";
import { BfxSysConfigUtilService } from "shared/auth-services";
import { OrderStateRenderer } from "shared/cubiq-services/model/orders/OrderStateRenderer";
import { BfxConfigIDRefHelper } from "shared/helper-services/bfx-config-id-ref-helper";
import { NullProtoObject } from "../../shared/helper-services/null-proto-object";

const MSG_NOMESSAGE = "";
const MSG_REJECT_SINGLE = "pause.reject.single";
const MSG_REJECT_MULTI = "pause.reject.multi";
const MSG_FAIL_MULTI = "pause.fail.multi";
const TERMINAL_STATES = [CubiqTS.OrderState.FILLED, CubiqTS.OrderState.EXPIRED];

const logger = LoggerService.for(__dirname);

@Component({
  selector: "bfx-order-pauseresume",
  templateUrl: "./html/bfx-order-pauseresume.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxOrderPauseResumeComponent
  implements GoldenLayoutComponent, OnDestroy, AfterViewInit
{
  /**
   * An array of all selected orders for pause/resume
   */
  public orderRecords: Array<OrderPauseResumeRecord> = [];
  public orderMap: NullProtoObject<OrderPauseResumeRecord> = Object.create(null);

  public monitoredStrategyIds = new Set<string>();
  public pendingOrderRecords: Map<string, OrderPauseResumeRecord> = new Map();

  public showClose: boolean = true;
  public showPause: boolean = true;
  public showResume: boolean = false;
  public showAbort: boolean = false;
  public showReject: boolean = false;
  public showAccept: boolean = false;
  public loading: boolean = true;

  public scrollWidth: string = "750px";
  public scrollHeight: string = "180px";
  public commentFocused: boolean = false;
  public mandatoryComment: boolean = false;
  public mandatoryCommentLoaded: boolean = false;
  public comment: string = "";
  public message: string = "";
  public parentPopout: GoldenLayoutPopout = null;
  public progress: number = 0;
  public progressStatus: ProgressStatus = ProgressStatus.WAITING;
  public tableDefinition: TableDefinition;
  public modelUpdateSubscription: Subscription;
  public hedgeModelUpdateSubscription: Subscription;
  public freezeButtonUpdates: boolean = false;

  public canAccept: boolean = false;
  public canReject: boolean = false;
  public traderUnrestricted: boolean = false;
  public activeUser: ActiveUser = null;
  public stateColumnDef: BfxColDef = null;
  public externalTraderGroups: Array<string> = null;
  public invalidComment: boolean = null;
  private isInCancelMode: boolean = false;

  private tableElement: any;
  private scrollTableElement: any;

  private scheduledGridRefresh: boolean = false;

  public gridOptions: GridOptions;
  public columnDefs: Array<ColDef | BfxColDef> = [];

  private refreshDelay: number = 0;
  public reasonColumnId: string = "reason";

  private action: BfxBlotterAction;
  private errorMessage: string;

  public viewInitComplete: boolean = false;
  public blotterInstance: BfxOrderPauseResumeComponent = null;
  public commentFieldState: FieldStateValue = "enabled";

  public control: any = {
    mode: true,
    containerHeight: 324,
    scrollbarConfig: {
      suppressScrollX: true,
    },
  };
  private _orderDescriptionHelper: OrderDescriptionHelper;
  private orderStateRenderer: OrderStateRenderer;

  constructor(
    protected columnSelectorService: BfxBaseBlotterColumnSelectorService,
    public orderActionService: OrderDataActionPromiseService,
    public orderAcceptService: OrderAcceptancePromiseService,
    public orderBlotterService: BfxBlotterOrdersService,
    public sysConfigService: SystemConfigPromiseService,
    public orderDataService: OrderDataPromiseLookupService,
    public hedgeDataService: HedgeOrderDataPrimaryLookupService,
    public ordersTableDefinitionFactory: OrdersTableDefinitionFactory,
    public tradeGroupService: TraderGroupPromiseLookupService,
    public activeUserService: ActiveUserProfilePromiseService,
    public systemConfigService: SystemConfigPromiseService,
    public blotterOrderActionService: BfxBlotterOrdersActionService,
    public translationService: TranslateService,
    public notificationService: NotificationService,
    private algoStrategyLookupService: AlgoStrategyLookupService,
    private sysConfigUtil: BfxSysConfigUtilService,
    public element: ElementRef,
    private cd: ChangeDetectorRef,
  ) {
    this.initialize();
    this.blotterInstance = this;
    this.orderStateRenderer = new OrderStateRenderer(this.translationService);
  }

  protected initialize(): void {
    this._orderDescriptionHelper = new OrderDescriptionHelper(
      this.tradeGroupService,
      this.algoStrategyLookupService,
      this.sysConfigUtil,
    );
    this.gridOptions = <GridOptions>{
      deltaRowDataMode: true,
      getRowNodeId: (data) => {
        if (data) return String(data["orderId"]);
        return "";
      },
      suppressContextMenu: true,
      headerHeight: 24,
      suppressColumnMoveAnimation: true,
      suppressDragLeaveHidesColumns: true,
      suppressScrollOnNewData: true,
      suppressFocusAfterRefresh: true,
      suppressAutoSize: true,
      showToolPanel: false,
      toolPanelSuppressSideButtons: true,
      enableFilter: true,
      enableBrowserTooltips: true,
      rowDeselection: true,
      onGridReady: (params) => {
        if (this.gridOptions != null) {
          this.gridOptions.api = params.api;
          this.gridOptions.columnApi = params.columnApi;

          if (this.gridOptions.api != null) {
            this.gridOptions.api.setRowData(this.orderRecords);
          }
        }
      },
      onGridSizeChanged: (params) => {},
    };

    // Column defs common(ish) with Cancel/Take/Force Take/Transfer/Pause
    this.columnDefs = [
      {
        headerName: "Order ID",
        field: "orderRef",
        editable: false,
        colId: "orderId",
        suppressMenu: true,
        omitFromPicker: false,
        hide: false,
        minWidth: 80,
        width: 125,
      },
      {
        headerName: "Order",
        field: "orderDescription",
        editable: false,
        colId: "order",
        suppressMenu: true,
        omitFromPicker: false,
        disableInPicker: true,
        hide: false,
        minWidth: 250,
        width: 350,
      },
      {
        headerName: "Trader Group",
        field: "traderGroupName",
        editable: false,
        colId: "traderGroupName",
        suppressMenu: true,
        omitFromPicker: false,
        disableInPicker: true,
        hide: false,
        minWidth: 90,
        width: 110,
      },
      {
        headerName: "Status",
        field: "orderStatus",
        editable: false,
        colId: "orderStatus",
        suppressMenu: true,
        omitFromPicker: false,
        disableInPicker: true,
        hide: false,
        minWidth: 90,
        width: 110,
      },
      {
        headerName: "Reason",
        field: "reason",
        editable: false,
        colId: "reason",
        suppressMenu: true,
        omitFromPicker: false,
        disableInPicker: true,
        hide: false,
        minWidth: 250,
        width: 350,
      },
    ];

    NativeDesktopSupport.prepareGridOptions(this.gridOptions, this.element.nativeElement);
    this.tableDefinition = this.ordersTableDefinitionFactory.createDef();
    this.stateColumnDef = this.tableDefinition.getColumnDefinition(COLUMN_ORDER_STATE);

    this.systemConfigService.hasRole(Roles.DI_TRADER_ACCEPT).then((value: boolean) => {
      this.canAccept = value;
    });

    this.systemConfigService.hasRole(Roles.DI_TRADER_REJECT).then((value: boolean) => {
      this.canReject = value;
    });

    this.systemConfigService.hasRole(Roles.DI_TRADER_UNRESTRICTED).then((value: boolean) => {
      this.traderUnrestricted = value;
    });

    this.activeUserService.getActiveUserData().then((data: ActiveUser) => {
      this.activeUser = data;
    });
  }

  ngOnDestroy() {
    this.viewInitComplete = false;
    if (this.modelUpdateSubscription != null) {
      this.modelUpdateSubscription.unsubscribe();
    }
    if (null != this.hedgeModelUpdateSubscription) {
      this.hedgeModelUpdateSubscription.unsubscribe();
    }
  }

  ngAfterViewInit() {
    this.viewInitComplete = true;
  }

  glInit(launchData: OrderPauseResumeLaunchData, container: any, state: any): void {
    this.action = launchData.action;

    this.showPause = launchData.action === BfxBlotterAction.ACTION_ORDER_PAUSE;
    this.showResume = launchData.action === BfxBlotterAction.ACTION_ORDER_RESUME;

    // this.errorMessage = "";

    //        let rows :  Array<any> = data.orders

    this.orderRecords = this.createOrderRecords(launchData.instructions, false);
    let orderCount = this.orderRecords.length;
    this.orderRecords.forEach((record: OrderPauseResumeRecord) => {
      this.orderMap[record.orderId] = record;
      if (record.strategyId != null) {
        this.monitoredStrategyIds.add(record.strategyId);
      }
    });

    if (orderCount >= 500) {
      this.refreshDelay = 500;
    } else if (orderCount >= 100) {
      this.refreshDelay = 100;
    } else if (orderCount >= 50) {
      this.refreshDelay = 50;
    }

    this.modelUpdateSubscription = this.orderDataService
      .getModelUpdateSubject()
      .subscribe(this.onModelUpdated.bind(this));
    this.hedgeModelUpdateSubscription = this.hedgeDataService
      .getModelUpdateSubject()
      .subscribe(this.onHedgeModelUpdated.bind(this));

    this.tradeGroupService.getAll().then((value: TradingGroupImpl[]) => {
      this.externalTraderGroups = value
        .filter((traderGroup: TradingGroupImpl) => traderGroup.isExternallyFilledFg)
        .map((traderGroup: TradingGroupImpl) => traderGroup.groupCode);
      this.orderRecords.forEach((orderRecord: OrderPauseResumeRecord) => {
        orderRecord.external = this.externalTraderGroups.includes(orderRecord.traderGroupName);
      });
    });
  }

  protected scheduleRefresh(): void {
    if (!this.scheduledGridRefresh) {
      this.scheduledGridRefresh = true;

      setTimeout(() => {
        this.orderRecords.forEach((record: OrderPauseResumeRecord) => {
          if (TERMINAL_STATES.includes(record.orderState)) {
            record.state = OrderPauseResumeRecordState.ABORT_REJECT;
            record.orderStatus = this.orderStateRenderer.getStateAsString(record.orderState);
          }
        });

        if (this.gridOptions && this.gridOptions.api) {
          this.gridOptions.api.refreshCells({
            columns: ["orderStatus", "reason"],
          });
        }
        this.determineActions();
        this.scheduledGridRefresh = false;
      }, this.refreshDelay);
    }
  }

  protected determineActions(): void {
    if (!this.freezeButtonUpdates && this.progressStatus == ProgressStatus.RUNNING) {
      this.hideAllActionButtons();

      let acceptanceRecords: Array<OrderPauseResumeRecord> = [];
      let failedRecordsCount: number = 0;
      let rejectRecordsCount: number = 0;
      let succeededRecordCount: number = 0;
      let inProgressCount: number = 0;
      let anyInternal: boolean = false;

      this.orderRecords.forEach((record: OrderPauseResumeRecord) => {
        if (record.state == OrderPauseResumeRecordState.ACCEPTANCE) {
          acceptanceRecords.push(record);
          anyInternal = anyInternal || !record.external;
        } else if (record.state == OrderPauseResumeRecordState.FAILED) {
          failedRecordsCount++;
        } else if (record.state == OrderPauseResumeRecordState.ABORT_REJECT) {
          rejectRecordsCount++;
        } else if (record.state == OrderPauseResumeRecordState.SUCCESS) {
          succeededRecordCount++;
        } else if (record.state == OrderPauseResumeRecordState.IN_PROGRESS) {
          inProgressCount++;
        }
      });

      let isInProgress: boolean = inProgressCount != 0;
      let hasAcceptance: boolean = acceptanceRecords.length > 0;

      if (!isInProgress) {
        if (hasAcceptance && !this.isInCancelMode) {
          if (this.traderUnrestricted) {
            this.showAccept = true;
            this.showReject = true;
          } else {
            let ownsEvery: boolean = acceptanceRecords.every((record: OrderPauseResumeRecord) => {
              let traderGroupId: string = record.tgId;

              return (
                record.state != OrderPauseResumeRecordState.ACCEPTANCE ||
                this.activeUser.tradingGroupsIds.includes(traderGroupId)
              );
            });

            this.showAccept = this.canAccept && ownsEvery;
            this.showReject = this.canReject && ownsEvery;
          }

          this.showAbort = anyInternal;
        } else {
          this.showAbort = false;
          this.showAccept = false;
          this.showReject = false;
        }
      }

      let finishedCount: number = failedRecordsCount + succeededRecordCount + rejectRecordsCount;
      this.progress =
        (this.orderRecords.length - inProgressCount) * (100 / this.orderRecords.length);

      let allFinished: boolean = finishedCount == this.orderRecords.length;
      if (allFinished) {
        if (succeededRecordCount == this.orderRecords.length) {
          this.success();
        } else {
          if (failedRecordsCount > 0) {
            this.fail(failedRecordsCount, MSG_FAIL_MULTI);
          } else if (rejectRecordsCount > 0) {
            if (rejectRecordsCount == 1) {
              this.fail(rejectRecordsCount, MSG_REJECT_SINGLE);
            } else {
              this.fail(rejectRecordsCount, MSG_REJECT_MULTI);
            }
          }
        }
      }
    }
    this.cd.markForCheck();
  }

  public abort(): void {
    this.isInCancelMode = true;
    this.hideAllActionButtons();
    let stratMap: Map<string, string> = new Map();
    let ids: Array<string> = this.orderRecords
      .filter((record: OrderPauseResumeRecord) => {
        if (record.state == OrderPauseResumeRecordState.ACCEPTANCE && !record.external) {
          record.state = OrderPauseResumeRecordState.IN_PROGRESS;
          if (null != record.strategyId) {
            if (!stratMap.get(record.strategyId)) {
              stratMap.set(record.strategyId, record.orderId);
              return true;
            } else {
              return false;
            }
          } else {
            // BMALGO
            return true;
          }
        }
        return false;
      })
      .map((record: OrderPauseResumeRecord) => record.orderId);
    let acceptanceType: AcceptanceType =
      this.action == BfxBlotterAction.ACTION_ORDER_PAUSE
        ? AcceptanceType.PAUSE
        : AcceptanceType.RESUME;
    this.orderAcceptService.requestAcceptance(ids, "", AcceptanceAction.ABORT, acceptanceType);
  }

  public accept(): void {
    this.isInCancelMode = true;
    this.hideAllActionButtons();

    let ordersExternal: Array<OrderPauseResumeRecord> = this.orderRecords.filter(
      (record: OrderPauseResumeRecord) =>
        record.state == OrderPauseResumeRecordState.ACCEPTANCE && record.external,
    );
    let externalCount: number = ordersExternal.length;

    let stratMap: Map<string, string> = new Map();
    let ids: Array<string> = this.orderRecords
      .filter((record: OrderPauseResumeRecord) => {
        if (record.state == OrderPauseResumeRecordState.ACCEPTANCE) {
          if (null != record.strategyId) {
            if (!stratMap.get(record.strategyId)) {
              stratMap.set(record.strategyId, record.orderId);
              return true;
            } else {
              return false;
            }
          } else {
            // BMALGO
            return true;
          }
        }
        return false;
      })
      .map((value: OrderPauseResumeRecord) => {
        value.state = OrderPauseResumeRecordState.IN_PROGRESS;
        return value.orderId;
      });

    if (externalCount > 0) {
      let msg: string =
        externalCount == 1 ? "cancel.order.external.single" : "cancel.order.external.multi";
      let args: Array<any> = externalCount == 1 ? [ordersExternal[0].orderId] : [externalCount];
      let payload: any = {
        messages: [this.translationService.instant(msg, args)],
        title: "warn.order.external.title",
        callback: this.onAccept.bind(this, ids),
        callbackNegative: () => {
          this.isInCancelMode = false;
          this.determineActions();
        },
      };
      this.openConfirmDialog(payload);
      this.determineActions();
    } else {
      this.onAccept(ids);
    }
  }

  protected onAccept(ids: Array<string>): void {
    this.orderAcceptService.requestAcceptance(
      ids,
      this.comment,
      AcceptanceAction.ACCEPT,
      this.action == BfxBlotterAction.ACTION_ORDER_PAUSE
        ? AcceptanceType.PAUSE
        : AcceptanceType.RESUME,
    );
  }

  public reject(): void {
    this.isInCancelMode = true;
    this.hideAllActionButtons();

    let ordersExternal: Array<OrderPauseResumeRecord> = this.orderRecords.filter(
      (record: OrderPauseResumeRecord) =>
        record.state == OrderPauseResumeRecordState.ACCEPTANCE && record.external,
    );
    let externalCount: number = ordersExternal.length;

    let stratMap: Map<string, string> = new Map();
    let ids: Array<string> = this.orderRecords
      .filter((record: OrderPauseResumeRecord) => {
        if (record.state == OrderPauseResumeRecordState.ACCEPTANCE) {
          record.state = OrderPauseResumeRecordState.IN_PROGRESS;
          if (null != record.strategyId) {
            if (!stratMap.get(record.strategyId)) {
              stratMap.set(record.strategyId, record.orderId);
              return true;
            } else {
              return false;
            }
          } else {
            // BMALGO
            return true;
          }
        }
        return false;
      })
      .map((value: OrderPauseResumeRecord) => value.orderId);

    if (externalCount > 0) {
      let msg: string =
        externalCount == 1 ? "cancel.order.external.single" : "cancel.order.external.multi";
      let args: Array<any> = externalCount == 1 ? [ordersExternal[0].orderId] : [externalCount];
      let payload: any = {
        messages: [this.translationService.instant(msg, args)],
        title: "cancel.order.external.title",
        callback: this.onReject.bind(this, ids),
        callbackNegative: () => {
          this.isInCancelMode = false;
          this.determineActions();
        },
      };
      this.openConfirmDialog(payload);
      this.determineActions();
    } else {
      this.onReject(ids);
    }
  }

  public onReject(ids: Array<string>): void {
    let acceptanceType: AcceptanceType =
      this.action == BfxBlotterAction.ACTION_ORDER_PAUSE
        ? AcceptanceType.PAUSE
        : AcceptanceType.RESUME;
    this.orderAcceptService.requestAcceptance(
      ids,
      this.comment,
      AcceptanceAction.REJECT,
      acceptanceType,
    );
  }

  /**
   * Create order cancel records for the table in this dialog
   * from the adapter row data
   * @param rows raw data rows as adapted in the cubiq services
   * @param withReason should reason be included in the record data
   */
  protected createOrderRecords(
    launchDataSet: Array<OrderPauseResumeInstruction>,
    withReason: boolean = true,
  ): Array<OrderPauseResumeRecord> {
    const initOrders = launchDataSet.map(
      (instruction: OrderPauseResumeInstruction) => instruction.order,
    );

    const relatedOrders = this.findRelatedOrders(initOrders);
    const allOrders = initOrders.concat(relatedOrders);

    return allOrders.map((order) => this.createOrderRecord(order, withReason));
  }

  /**
   * Get all related orders for limit strategies
   * @param rows
   * @returns
   */
  protected findRelatedOrders(orders: OrderDataImpl[]): OrderDataImpl[] {
    const limits: OrderDataImpl[] = [];
    for (let order of orders) {
      switch (order.orderType) {
        case OrderType.LIMIT_TP:
        case OrderType.LIMIT_SL:
        case OrderType.LIMIT_CALL:
          limits.push(order);
      }
    }
    if (limits.length > 0) {
      const relatedLimitOrders = this.orderBlotterService.findRelatedOrders(limits);
      return relatedLimitOrders;
    }
    return [];
  }

  /**
   * Creates a order cancel record from raw adapter order data records,
   * formulating the order info, trader group name, any reasons if
   * available
   * @param row the raw order data record
   * @param withReason should the reason be included in the record data
   */
  protected createOrderRecord(
    order: OrderDataImpl,
    withReason: boolean = true,
  ): OrderPauseResumeRecord {
    let orderRecord: OrderPauseResumeRecord = new OrderPauseResumeRecord();

    orderRecord.orderId = order.orderId == null ? null : order.orderId.toString();
    orderRecord.strategyId = order.strategyId == null ? null : order.strategyId.toString();
    orderRecord.tgId = order.deskId.toString();
    orderRecord.orderRef = BfxConfigIDRefHelper.getOrderRef(order);
    orderRecord.orderDescription = this._orderDescriptionHelper.createOrderDescription(order);

    let pauseState: PauseState = order.pauseState; // row[COLUMN_PAUSE_STATE];
    if (!pauseState) {
      pauseState = PauseState.NOT_PAUSED;
    }

    if (
      order.orderType === OrderType.MARKET ||
      order.orderType === OrderType.MARKET_FOK ||
      order.orderType === OrderType.MARKET_IOC
    ) {
      const msgType = this.showPause ? "pause" : "resume";
      orderRecord.orderStatus = this.translationService.instant(`order.${msgType}.action.rejected`);
      orderRecord.reason = this.translationService.instant(`order.${msgType}.fail.market.order`, [
        order.orderRef,
      ]);
      orderRecord.state = OrderPauseResumeRecordState.SUCCESS;
      orderRecord.eligibleForPause = false;
    } else if (this.showPause && pauseState == PauseState.PAUSED) {
      orderRecord.orderStatus = this.translationService.instant("order.pause.action.rejected");
      orderRecord.reason = this.translationService.instant("order.pause.fail.already.paused");
      orderRecord.state = OrderPauseResumeRecordState.SUCCESS;
      orderRecord.eligibleForPause = false;
    } else if (this.showResume && pauseState == PauseState.NOT_PAUSED) {
      orderRecord.orderStatus = this.translationService.instant("order.resume.action.rejected");
      orderRecord.reason = this.translationService.instant("order.resume.not.paused", [
        order.orderRef,
      ]);
      orderRecord.state = OrderPauseResumeRecordState.SUCCESS;
      orderRecord.eligibleForPause = false;
    } else {
      orderRecord.eligibleForPause = true;
      if (order.pauseState === PauseState.PAUSED) {
        orderRecord.orderStatus = this.translationService.instant("order.state.PAUSED");
      } else {
        orderRecord.orderStatus = "";
      }
    }
    orderRecord.traderGroupName = this._orderDescriptionHelper.createTraderGroupDescription(
      order,
      false,
    );

    if (withReason) {
      orderRecord.reason = order.orderErrors?.toString() ?? null; // row[COLUMN_ORDER_ERRORS];
    }

    return orderRecord;
  }

  private orderStateDesc(order: OrderDataImpl): string {
    let ret = null;

    return ret;
  }

  /**
   * Transforms progress status into animation state name
   */
  // public getAnimateState(): string {
  //   return ProgressStatus[this.progressStatus].toLowerCase();
  // }

  public showColumnSelector(event): void {
    this.columnSelectorService.doColumnSelectorMenu(this, this.gridOptions, event);
  }

  // handle the click of the ag-grid column header and header
  public handleClick(event) {
    if (event.which == 3 && event.target.className.indexOf("ag-header-cell") !== -1) {
      this.showColumnSelector(event);
    } else if (event.which == 3 && event.target.className.indexOf("ag-header-container") !== -1) {
      this.columnSelectorService.doColumnSelectorMenu(this, this.gridOptions, event);
    }
  }

  setParentPopup(parentPopup: GoldenLayoutPopout) {
    this.parentPopout = parentPopup;
    this.parentPopout.setShowDock(false);
  }

  /**
   * Invoked by the close confirmation warning dialog if the
   * user chooses to go ahead and close the window
   */
  public confirmClose(): void {
    this.progressStatus = ProgressStatus.FAILURE;
    this.parentPopout.close();
  }

  /**
   * Callback that determines if this popup should be closed or not
   */
  public onClose(): boolean {
    if (this.progressStatus == ProgressStatus.RUNNING) {
      let orderCount: number = this.orderRecords.length;
      let msg: string =
        orderCount == 1 ? "cancel.order.confirm.single" : "cancel.order.confirm.multi";
      let payload: any = {
        messages: [this.translationService.instant(msg)],
        title: "confirm.close.title",
        callback: this.confirmClose.bind(this),
      };
      this.openConfirmDialog(payload);
      return false;
    }

    return true;
  }

  /**
   *
   */
  protected openConfirmDialog(payload: any): void {
    let bfxDialogData: BfxDialogData = new BfxDialogData(
      BfxDialogCommand.DIALOG_ORDER_CANCEL_CONFIRM_OPEN,
      null,
      null,
      payload,
    );

    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      let wnd: NativeDesktopWindow = NativeDesktopSupport.getWindow(this.element.nativeElement);
      bfxDialogData.body = wnd.getNativeWindow().document.body;
    }

    this.blotterOrderActionService.launchNewDialog(bfxDialogData);
  }

  public pauseOrders(event: any, commentInput: any): void {
    this.actionOrders(event, commentInput, BfxBlotterAction.ACTION_ORDER_PAUSE);
  }

  public resumeOrders(event: any, commentInput: any): void {
    this.actionOrders(event, commentInput, BfxBlotterAction.ACTION_ORDER_RESUME);
  }

  /**
   * Invoked when the 'Action' button is pressed. In case the
   * comment is mandatory the comment input will be focussed, otherwise
   * the flow is run.
   * @param event the event that triggered invocation of this method
   * @param commentInput the comment input reference
   */
  protected actionOrders(event: any, commentInput: any, action: BfxBlotterAction): void {
    if (this.mandatoryComment && this.comment.trim().length == 0) {
      this.invalidComment = true;
      commentInput.focus();
    } else {
      let stratMap: Map<string, string> = new Map();
      let orderIds: Array<string> = [];
      this.orderRecords.forEach((order) => {
        if (order.eligibleForPause) {
          if (null == order.strategyId) {
            // BMALGO
            orderIds.push("" + order.orderId);
          } else {
            if (!stratMap.get(order.strategyId)) {
              orderIds.push("" + order.orderId);
              stratMap.set(order.strategyId, order.orderId);
            }
          }
        }
      });
      let refresh = false;
      this.pendingOrderRecords.forEach((record: OrderPauseResumeRecord, orderId, map) => {
        map.delete(orderId);
        this.orderMap[record.orderId] = record;
        this.orderRecords.push(record);
        refresh = true;
      });
      if (refresh) {
        this.gridOptions?.api?.setRowData(this.orderRecords);
      }

      this.hideAllActionButtons();
      this.progressStatus = ProgressStatus.RUNNING;
      let request: OrderPauseResumeRequestImpl = new OrderPauseResumeRequestImpl();
      (request.orderIds = orderIds), (request.reason = this.comment);
      request.action = PauseResumeAction.PAUSE;
      switch (action) {
        case BfxBlotterAction.ACTION_ORDER_PAUSE: {
          request.action = PauseResumeAction.PAUSE;
          break;
        }
        case BfxBlotterAction.ACTION_ORDER_RESUME: {
          request.action = PauseResumeAction.RESUME;
          break;
        }
      }

      this.orderActionService
        .pauseResumeOrders(request)
        .then((response: StandardReportImpl) => {
          let errors: ErrorsImpl = new ErrorsImpl();

          if (response.encodableType == errors.encodableType) {
            Object.assign(errors, response);
            this.handleErrorResult(errors);
          } else if (response.results) {
            let results: Array<OrderPauseResumeResponseImpl> = response.results;
            let erroredOrderIdSet = new Set<string>();

            results.forEach((result: OrderPauseResumeResponseImpl) => {
              let tableRow: OrderPauseResumeRecord = this.orderMap[result.orderId];

              if (result.errors) {
                erroredOrderIdSet = this.handlePauseStatusUpdate(
                  tableRow,
                  result.orderId,
                  result.state,
                  result.errors,
                );
              }

              if (erroredOrderIdSet.size == this.orderRecords.length) {
                this.fail(erroredOrderIdSet.size);
              }

              this.scheduleRefresh();
            });
          } else {
            this.fail(this.orderRecords.length);
          }
        })
        .catch((reason: any) => {
          this.fail(this.orderRecords.length);
        });
    }

    this.determineActions();
  }

  private handleErrorResult(errors: ErrorsImpl) {
    let rowIds = new Array<any>();

    this.orderRecords.forEach((record: OrderPauseResumeRecord) => {
      record.state = OrderPauseResumeRecordState.FAILED;
      record.orderStatus = STATUS_FAILED;
      this.updateOrderReason(record, errors.errorMessages);
      //
      rowIds.push(record.orderId);
    });

    // redraw
    if (rowIds.length > 0) {
      this.redrawRowsWithIds(rowIds);
    }

    this.fail(this.orderRecords.length);
    this.cd.markForCheck();
  }

  public validateCommentInput(_event): void {
    this.comment = this.comment.trim();
    this.invalidComment = this.mandatoryComment && this.comment.length == 0;
    this.commentFocused = false;
    if (this.invalidComment) {
      this.commentFieldState = "invalid";
      this.cd.markForCheck();
    }
  }

  /**
   * Updates order cancel record reason field if error is properly supplied
   */
  protected updateOrderReason(orderRecord: OrderPauseResumeRecord, errors: Array<any>): void {
    if (errors != null && errors.length > 0) {
      orderRecord.reason = this.convertErrors(errors);
    }
  }

  /**
   * Creates a translation object with a message as id and
   * parameters as args list from a string
   * @param error the error string as served in ErrorsImpl object
   */
  protected convertErrors(errors: Array<any>): string {
    let message = "";
    errors
      .map((e) => JSON.parse(e))
      .forEach((e) => {
        if (e["id"] && e["id"].length > 0)
          message += this.translationService.instant(e["id"], e["args"]) + "\n";
      }) + "  ";

    if (message.length > 0) {
      return message;
    }
    return "-";
  }

  /**
   * Convenience method that sets the final state of the window to success.
   * @param errorCount the number of messages that have failed/rejected
   * @param message the message or default error message if left out
   */
  protected success(): void {
    this.hideAllActionButtons();
    this.progress = 100;
    this.showClose = true;
    this.progressStatus = ProgressStatus.SUCCESS;
  }

  /**
   * Convenience method that sets the final state of the window to failure.
   * @param errorCount the number of messages that have failed/rejected
   * @param message the message or default error message if left out
   */
  protected fail(errorCount: number, message: string = this.errorMessage): void {
    this.message = message ? this.translationService.instant(message, [errorCount]) : MSG_NOMESSAGE;
    this.hideAllActionButtons();
    this.progress = 100;
    this.showClose = true;
    this.progressStatus = ProgressStatus.FAILURE;
  }

  /**
   * Hides all action buttons placed at the bottom right of the dialog (abort, accept, reject, cancel and close)
   * @param freeze indicates if further button determination should occur
   */
  protected hideAllActionButtons(freeze: boolean = false): void {
    this.freezeButtonUpdates = freeze;
    this.showAbort =
      this.showAccept =
      this.showPause =
      this.showResume =
      this.showClose =
      this.showReject =
        false;
  }

  private redrawRowsWithIds(rowIds: Array<string>): void {
    let rows = new Array<any>();
    rowIds.forEach((rowId: string) => {
      let rowData = this.gridOptions.api.getRowNode(rowId);
      if (rowData != null) {
        rows.push(rowData);
      }
    });
    if (rows != null && rows.length >= 1) {
      this.gridOptions.api.redrawRows({ rowNodes: rows });
    }
  }

  protected onModelUpdated(modelEvent: ModelUpdate): void {
    if (modelEvent instanceof Array) {
      this.handleModelUpdated(modelEvent);
    } else {
      this.handleModelUpdated([modelEvent]);
    }
  }

  protected onHedgeModelUpdated(modelEvent: Array<any>): void {
    this.handleModelUpdated([modelEvent]);
  }

  protected handleModelUpdated(modelEvent: Array<any>): void {
    modelEvent.forEach((modelObj: any) => {
      if (modelObj == null) {
        return;
      }

      this.updateOrderState(modelObj);

      if (modelObj.pauseState == null) return;

      let tableRow: OrderPauseResumeRecord = this.orderMap[modelObj.orderId];
      if (tableRow && modelObj.source != OrderDataModelUpdateSource.LockLifeCycleEvent) {
        this.handlePauseStatusUpdate(
          tableRow,
          modelObj.orderId,
          modelObj.pauseState,
          modelObj.errors,
        );
        this.scheduleRefresh();
      }
    });
  }

  private handlePauseStatusUpdate(
    tableRow: OrderPauseResumeRecord,
    orderId: string,
    state: PauseState,
    errors: ErrorsImpl,
  ): Set<string> {
    let ret = new Set<string>();

    let isPause: boolean = this.action == BfxBlotterAction.ACTION_ORDER_PAUSE;

    // Skip updates if no requests were made
    if ((isPause && this.showPause) || (!isPause && this.showResume)) {
      return;
    }

    if (tableRow != null) {
      if (errors) {
        this.updateOrderReason(tableRow, errors.errorMessages);
        ret.add(orderId);
      }

      switch (state) {
        case PauseState.PAUSED: {
          tableRow.state = isPause
            ? OrderPauseResumeRecordState.SUCCESS
            : OrderPauseResumeRecordState.FAILED;
          tableRow.orderStatus = isPause ? "Order Paused" : "Resume Failed";
          break;
        }
        case PauseState.NOT_PAUSED: {
          tableRow.state = isPause
            ? OrderPauseResumeRecordState.FAILED
            : OrderPauseResumeRecordState.SUCCESS;
          tableRow.orderStatus = isPause ? "Pause Failed" : "Order Resumed";
          break;
        }
        case PauseState.PAUSE_PENDING: {
          tableRow.state = OrderPauseResumeRecordState.ACCEPTANCE;
          tableRow.orderStatus = "Pause Pending";
          break;
        }
        case PauseState.RESUME_PENDING: {
          tableRow.state = OrderPauseResumeRecordState.ACCEPTANCE;
          tableRow.orderStatus = "Resume Pending";
          break;
        }
      }
    }
    return ret;
  }

  updateOrderState(event: any) {
    const orderId = event.orderId == null ? null : event.orderId.toString();
    let orderRecord: OrderPauseResumeRecord = this.orderMap[orderId] ?? null;

    if (orderRecord != null) {
      orderRecord.orderState = event.orderState;
    } else if (orderRecord == null && this.monitoredStrategyIds.has(event.strategyId?.toString())) {
      orderRecord = this.createOrderRecord(event, false);

      this.pendingOrderRecords.set(orderId, orderRecord);
    }
  }
}
